<template lang="pug">
    .table-grid(:style='custom')
        table.dat-table(:class='isLoading ? "--loading" : ""')
            thead.dat-thead
                th.dat-header(
                        v-if='enableMultiSelect',
                        :key='"select-header"',
                        style='width: 30px;'
                )
                    checkbox-input-component(:value="{ id: 'select-all', selecionado: all }" @input="toggleSelectAll")
                th.dat-header(
                    v-for='(header, headerIndex) in this.headers',
                    :key='headerIndex',
                    :class='"--" + header.field',
                    :style='{ width: header.width, maxWidth: header.maxWidth, minWidth: header.minWidth }'
                )
                    span(v-if='header.tooltip', v-tooltip='{ label: header.tooltip, position: "bottom" }') {{ header.text }}
                    span(v-else) {{ header.text }}
            tbody
                template(v-for='(item, index) in this.data')
                    tr.dat-row(
                        :key='"row-" + index'
                        :style='item.faltaAssinaturaGO ? {backgroundColor: "var(--secondary-light-green)"} : null'
                    )
                        td.dat-cell(v-if='enableMultiSelect')
                            checkbox-input-component(:value="{ id: String(index), selecionado: isSelected(item) }" @input="toggleSelectItem(item)")

                        td.dat-cell(v-for='(label, labelIndex) in headers', :key='labelIndex')
                            slot(:name='label.field', :item='item')
                                span {{ item[label.field] || '-' }}
                    
                    slot(name='row-after' :item='item' :index='index')

        .empty(v-if='this.data.length == 0')
            b Sem dados
        .pagination(v-if='isPaginated')
            .items-per-page-selector
                span.label-items-per-page Itens por página:
                el-select(
                    v-model='selectedItemsPerPage',
                    @change='onItemsPerPageChange',
                    size='small',
                    style='width: 100px; margin-left: 8px;'
                )
                    el-option(
                        v-for='option in itemsPerPageOptions',
                        :key='option.value',
                        :label='option.label',
                        :value='option.value'
                    )
            .pagination-controls
                .page-guide(
                    v-for='(page, paginationIndex) in pageIcons',
                    :key='paginationIndex',
                    :class='[page.position === "CURRENT" ? "--current" : "", setPaginationChange(pagination.page, pagination.totalPages, page.isDisabled)]',
                    @click='onPagination(page)'
                )
                    .page-icon.mdi(v-if='page.position !== "CURRENT"' :class='page.icon')
                    span.page-icon(v-else) {{ pagination.page }} de {{ pagination.totalPages }}
                b.number-per-page Exibindo {{ paginacaoExibindoQuantidade }} de {{ pagination.totalItems }} itens.
</template>

<script>
import ButtonComponent from './ButtonComponent.vue';
import CheckboxInputComponent from './CheckboxInputComponent.vue';

export default {
    name: 'GridTableComponent',

    components: {
        ButtonComponent,
        CheckboxInputComponent,
    },

    props: {
        id: {
            type: String,
            default: () => `table-${Math.ceil(Math.random() * 10000)}`,
        },

        headers: {
            type: Array,
            default: null,
        },

        data: {
            type: Array,
            default: null,
        },

        isPaginated: {
            type: Boolean,
            default: false,
        },

        isLoading: {
            type: Boolean,
            default: false,
        },

        pagination: {
            type: Object,
            default: () => ({
                page: 1,
                totalItems: null,
                totalPages: 1,
                itemsPerPage: 10,
                order: 'ASC',
            }),
        },

        custom: {
            type: String,
            default: ''
        },

        enableMultiSelect: {
            type: Boolean,
            default: false,
        },
        selectedItemsGlobal: {
            type: Array,
            default: () => [],
        },
        selectedItemKey: {
            type: String,
            default: '',
        },
    },

    data: () => ({
        pageIcons: [
            { position: 'FIRST', icon: 'mdi-page-first', isDisabled: false },
            { position: 'LEFT', icon: 'mdi-chevron-left', isDisabled: false },
            { position: 'CURRENT', icon: null, isDisabled: false },
            { position: 'RIGHT', icon: 'mdi-chevron-right', isDisabled: false },
            { position: 'LAST', icon: 'mdi-page-last', isDisabled: false },
        ],

        paginationFromProps: null,
        selectedItems: [],
        all: false,
        selectedItemsPerPage: 10,
    }),

    methods: {
        toggleSelectAll() {
            // Se todos estiverem selecionados, deselecte todos da página atual
            if (this.all) {
                this.selectedItems = [];
            } else {
                this.selectedItems = [...this.data]; // Seleciona todos os itens da página atual
            }
            this.all = !this.all; // Inverte o estado de seleção total

            this.$emit('selection:changed', {
                selectedItems: this.selectedItems,
                all: this.all,
            });
        },

        toggleSelectItem(item) {
            const index = this.selectedItems.indexOf(item);
            if (index === -1) {
                this.selectedItems.push(item);
            } else {
                this.selectedItems.splice(index, 1);
            }

            this.all = this.selectedItems.length === this.data.length && this.data.length > 0;

            this.$emit('selection:changed', {
                selectedItems: this.selectedItems,
                all: this.all,
            });
        },

        isSelected(item) {
            return this.selectedItems.includes(item);
        },

        isSameSelectedItem(selectedItem, dataItem) {
            if (selectedItem === dataItem) {
                return true;
            }

            if (this.selectedItemKey) {
                const selectedValue = selectedItem?.[this.selectedItemKey];
                const dataValue = dataItem?.[this.selectedItemKey];
                return selectedValue !== undefined && selectedValue !== null && selectedValue === dataValue;
            }

            return selectedItem.numeroDoCAR && selectedItem.numeroDoCAR === dataItem.numeroDoCAR && selectedItem.nomeArea === dataItem.nomeArea;
        },

        syncSelectedItemsWithGlobal() {
            if (!this.data || this.data.length === 0) {
                this.selectedItems = [];
                this.all = false;
                return;
            }

            // Mantém selecionados apenas os itens da página atual que estejam na lista global
            // Usa comparação por numeroDoCAR+nomeArea como fallback já que itens podem não ter id único
            this.selectedItems = this.data.filter(dataItem =>
                this.selectedItemsGlobal.some(sel => this.isSameSelectedItem(sel, dataItem))
            );

            this.all = this.selectedItems.length === this.data.length && this.data.length > 0;
        },

        setPaginationChange(page, totalPages, isDisabled) {
            if (page > 1) {
                this.pageIcons[0].isDisabled = false;
                this.pageIcons[1].isDisabled = false;
            } else {
                this.pageIcons[0].isDisabled = true;
                this.pageIcons[1].isDisabled = true;
            }
            if (page >= totalPages) {
                this.pageIcons[3].isDisabled = true;
                this.pageIcons[4].isDisabled = true;
            } else {
                this.pageIcons[3].isDisabled = false;
                this.pageIcons[4].isDisabled = false;
            }

            return isDisabled ? '--disabled' : '';
        },

        changePagination(page) {
            switch (page.position) {
                case 'FIRST':
                    this.pagination.page = 1;
                    break;
                case 'LEFT':
                    this.pagination.page -= 1;
                    break;
                case 'RIGHT':
                    this.pagination.page += 1;
                    break;
                case 'LAST':
                    this.pagination.page = this.pagination.totalPages;
                    break;
            }
        },

        onPagination(page) {
            if (page.position === 'CURRENT') {
                return;
            }
            this.changePagination(page);
            this.$emit('table:page-changed', this.pagination);
        },

        onItemsPerPageChange(value) {
            const itemsPerPage = value === -1 ? this.pagination.totalItems : value;
            this.$emit('table:items-per-page-changed', itemsPerPage);
        },
    },

    computed: {
        paginacaoExibindoQuantidade() {
            const inicial = (this.pagination.page - 1) * this.pagination.itemsPerPage + 1;
            const final = Math.min(this.pagination.page * this.pagination.itemsPerPage, this.pagination.totalItems);
            return `${inicial} - ${final}`;
        },
        itemsPerPageOptions() {
            const totalItems = this.pagination?.totalItems || 0;
            const baseOptions = [
                { value: 10, label: '10' },
                { value: 25, label: '25' },
                { value: 50, label: '50' },
                { value: 100, label: '100' },
            ];
            
            // Se total < 10, mostrar apenas "10"
            if (totalItems < 10) {
                return [{ value: 10, label: '10' }];
            }
            
            // Filtra opções que são menores que o total
            const filteredOptions = baseOptions.filter(option => option.value <= totalItems);
            
            // Adiciona "Todos" apenas se total >= 10
            if (totalItems >= 10) {
                filteredOptions.push({ value: -1, label: `Todos (${totalItems})` });
            }
            
            return filteredOptions;
        },
    },

    mounted() {
        this.syncSelectedItemsWithGlobal();
        this.selectedItemsPerPage = this.pagination.itemsPerPage || 10;
    },

    watch: {
        pagination: {
            handler(newVal) {
                this.setPaginationChange(this.pagination.page, this.pagination.totalPages);
                if (newVal.itemsPerPage && newVal.itemsPerPage !== this.selectedItemsPerPage) {
                    this.selectedItemsPerPage = newVal.itemsPerPage;
                }
                
                // Ajusta selectedItemsPerPage se não estiver mais disponível nas opções
                const availableValues = this.itemsPerPageOptions.map(opt => opt.value);
                if (!availableValues.includes(this.selectedItemsPerPage) && availableValues.length > 0) {
                    // Define para a maior opção disponível (que não seja -1/Todos)
                    const numericOptions = availableValues.filter(v => v > 0);
                    this.selectedItemsPerPage = numericOptions[numericOptions.length - 1] || 10;
                }
            },
            deep: true,
        },
        'pagination.page'(newPage, oldPage) {
            // Ao mudar de página, sincroniza selectedItems com os itens da página atual que estão na lista global
            if (oldPage && newPage !== oldPage) {
                this.syncSelectedItemsWithGlobal();
            }
        },
        data(newData) {
            // Quando os dados mudam (mudança de página), sincroniza com a lista global
            if (newData && newData.length > 0) {
                this.syncSelectedItemsWithGlobal();
            } else {
                this.selectedItems = [];
                this.all = false;
            }
        },
        selectedItemsGlobal: {
            handler() {
                this.syncSelectedItemsWithGlobal();
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss">
.table-grid {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.dat-table {
    width: 100%;

    border-radius: 5px;
    border: 0.3px solid var(--primary-dark-smoke);
    text-align: left;
}

.dat-header {
    padding: 3px 10px;
    height: 30px;

    color: black;
    border-bottom: 0.3px solid var(--primary-dark-smoke);
}

table thead,
table tbody {
    width: 100%;
}

table.--loading tbody {
    position: relative;
}

table.--loading tbody:after {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--primary-smoke);
    background-image: url('../../assets/loading.gif');
    background-position: center;
    background-repeat: no-repeat;
    background-size: 50px 50px;
    content: '';
}

.dat-row {
    height: 30px;
}

.dat-cell {
    max-width: 300px;
    word-break: break-word;
    padding: 3px 10px;

    border-bottom: 0.3px solid var(--primary-dark-smoke);
}

.pagination {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 20px;

    width: 100%;
    padding-top: 15px;
}

.items-per-page-selector {
    display: flex;
    align-items: center;
    font-size: 12px;
    font-weight: 600;
}

.label-items-per-page {
    color: black;
}

.pagination-controls {
    display: flex;
    align-items: center;
    gap: 5px;
}

.page-guide {
    padding: 3px;
    margin: 1px;
    height: 30px;
    width: 30px;
    display: flex;
    justify-content: center;
    align-items: center;

    background-color: var(--primary-light-green);
    border: 1px solid var(--primary-green);

    cursor: pointer;
}

.page-guide:active {
    opacity: 1;
    transition: 1s;
    background-color: var(--primary-green);
}

.page-icon {
    color: var(--primary-white) !important;
    line-height: 20px;
}

.--current {
    text-align: center;
    width: 60px;
    cursor: default;
    background-color: var(--primary-green);
}

.--disabled {
    pointer-events: none;
    background-color: var(--primar-disable-gray);
    cursor: not-allowed;
    border: 0pc;
}

.number-per-page {
    padding-left: 20px;
}

.empty {
    display: flex;
    color: var(--primary-dark-smoke);
    justify-content: center;
    padding: 12px;
    font-size: 18px;
}
</style>
